import React, { useState, useEffect } from "react";
import { fetchFare } from "../lib/api";
import { Calculator, Info } from "lucide-react";

export default function FareCalculator() {
    const [miles, setMiles] = useState(10);
    const [waitOver1hr, setWaitOver1hr] = useState(false);
    const [isMedical, setIsMedical] = useState(false);
    const [fare, setFare] = useState(null);

    useEffect(() => {
        if (miles >= 1) {
            fetchFare(miles).then(setFare).catch(() => setFare(null));
        }
    }, [miles]);

    const computeDisplay = () => {
        if (!fare) return null;
        let value = waitOver1hr ? fare.wait_over_1hr : fare.wait_under_1hr;
        let note = waitOver1hr ? "Wait time over 1 hour (+£2)" : "Wait time under 1 hour";
        let capped = false;
        if (isMedical && value > 8) {
            value = 8;
            note = "Medical journey — capped at £8";
            capped = true;
        }
        return { value, note, capped };
    };

    const disp = computeDisplay();

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10 lg:py-14 fade-up" data-testid="fare-page">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E57373] font-semibold mb-3">Tool</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Outfit" }}>Fare Calculator</h1>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Based on the Car Scheme Ready Reckoner. First 10 miles: minimum £5. Each mile thereafter: 50p.
            </p>

            <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 mb-8">
                <label className="block text-sm font-medium text-gray-900 mb-2" htmlFor="miles">Passenger miles</label>
                <div className="flex items-center gap-4">
                    <input
                        id="miles"
                        type="range"
                        min={1}
                        max={60}
                        value={miles}
                        onChange={e => setMiles(parseInt(e.target.value))}
                        className="flex-1 accent-[#2C7A85]"
                        data-testid="fare-slider"
                    />
                    <input
                        type="number"
                        min={1}
                        value={miles}
                        onChange={e => setMiles(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-md text-center text-lg font-medium"
                        data-testid="fare-miles-input"
                    />
                    <span className="text-gray-500 text-sm">miles</span>
                </div>

                <div className="mt-6 space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-wait">
                        <input
                            type="checkbox"
                            checked={waitOver1hr}
                            onChange={e => setWaitOver1hr(e.target.checked)}
                            className="h-5 w-5 accent-[#2C7A85]"
                        />
                        <span className="text-[15px] text-gray-700">Wait time over 1 hour (+£2)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-medical">
                        <input
                            type="checkbox"
                            checked={isMedical}
                            onChange={e => setIsMedical(e.target.checked)}
                            className="h-5 w-5 accent-[#2C7A85]"
                        />
                        <span className="text-[15px] text-gray-700">Medical journey (capped at £8)</span>
                    </label>
                </div>
            </div>

            {disp && (
                <div className="bg-[#2C7A85] text-white rounded-lg p-8" data-testid="fare-result">
                    <div className="flex items-start gap-4">
                        <Calculator className="h-6 w-6 mt-1 text-[#E57373] flex-shrink-0" />
                        <div>
                            <div className="text-sm uppercase tracking-widest opacity-70 mb-1">Passenger charge</div>
                            <div className="text-5xl sm:text-6xl font-bold" style={{ fontFamily: "Outfit" }}>£{disp.value.toFixed(2)}</div>
                            <div className="text-sm mt-3 opacity-80">{disp.note}</div>
                            {disp.capped && (
                                <div className="text-xs mt-1 opacity-70">Original would have been £{(waitOver1hr ? fare.wait_over_1hr : fare.wait_under_1hr).toFixed(2)}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-8 bg-amber-50 border-l-4 border-[#E57373] p-5 rounded-r-md flex gap-3">
                <Info className="h-5 w-5 text-[#E57373] flex-shrink-0 mt-0.5" />
                <div className="text-[15px] text-gray-700 leading-relaxed">
                    <strong>Wait time over 1.5 hours</strong> on short journeys is charged as two separate trips.
                    Raigmore / NTC runs use a separate pricing mechanism.
                </div>
            </div>
        </div>
    );
}
